"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HeartHandshake, Wind, Sparkles, SunMedium, Flower2, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface StepItem {
  id: number;
  number: string;
  title: string;
  duration: string;
  description: React.ReactNode;
  details: string;
  icon: React.ElementType;
}
const steps: StepItem[] = [{
  id: 1,
  number: "01",
  title: "Ankommen & Erstgespräch",
  duration: "ca. 30 Minuten",
  description: "In ruhiger Atmosphäre nehme ich mir Zeit für ein einfühlsames Gespräch über Deine aktuellen Bedürfnisse und Themen.",
  details: "Gemeinsam definieren wir die Intention für die Behandlung, damit Du vollkommen entspannt und geerdet ankommen kannst.",
  icon: HeartHandshake
}, {
  id: 2,
  number: "02",
  title: "Vorbereitung & Einstimmung",
  duration: "ca. 2 Minuten",
  description: "Du legst dich bekleidet und bequem auf die Behandlungsliege. Sanfte Klänge und eine bewusste Atemübung leiten die Entspannung ein.",
  details: "Wärmende Decken und ergonomische Kissen sorgen für maximalen Komfort während der gesamten Sitzung.",
  icon: Wind
}, {
  id: 3,
  number: "03",
  title: "Die Reiki-Behandlung",
  duration: "ca. 50 Minuten",
  description: <>
      Durch sanftes Handauflegen entlang der{" "}
      <Link to="/ChakraHealingPage" className="underline cursor-pointer">
        Energiezentren (Chakren)
      </Link>{" "}
      wird der Fluss Deiner Lebensenergie harmonisiert.
    </>,
  details: "Blockaden dürfen sich lösen; tiefe körperliche und mentale Regeneration setzt ein, während wohltuende Wärme spürbar wird.",
  icon: SunMedium
}, {
  id: 4,
  number: "04",
  title: "Sanftes Erwachen & Nachruhen",
  duration: "ca. 5 Minuten",
  description: "Nach der Behandlung verweilst du noch einige Momente in der Stille, um die Wirkung vollständig im Körper zu integrieren.",
  details: "\xA0Sanftes Zurückkehren in das Hier und Jetzt.",
  icon: Sparkles
}, {
  id: 5,
  number: "05",
  title: "Reflexion & Alltagstipps",
  duration: "ca. 5 Minuten",
  description: "Wir tauschen aufgetretene Wahrnehmungen aus und besprechen einfache Impulse zur Unterstützung im Alltag.",
  details: "Du erhältst \xA0konkrete Empfehlungen, um die gewonnene Klarheit und Balance langfristig zu bewahren.",
  icon: Flower2
}];
export default function ReikiProcess() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="reikisessionprocess" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-sans text-xs tracking-wide">
            Ablauf & Erfahrung
          </Badge>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-foreground mb-6">Was Dich bei einer Sitzung erwartet</h2>
          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed">Jede Reiki-Behandlung bei Wandelherz in Zürich, Kreis 6 ist ein geschützter Raum der Achtsamkeit. Hier erfährst Du Schritt für Schritt, wie eine Einzelsitzung strukturiert ist.</p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2 bg-border" />

          <div className="space-y-10 lg:space-y-12">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isEven = i % 2 === 1;
            return <div key={step.id} data-index={i} className="relative">
                  <motion.div initial="hidden" whileInView="visible" viewport={{
                once: true,
                amount: 0.2
              }} variants={cardVariants} className={`flex flex-col lg:flex-row items-center gap-8 ${isEven ? "lg:flex-row-reverse" : ""}`}>
                    <div className="w-full lg:w-1/2">
                      <Card className="border border-border/70 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 bg-card rounded-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between gap-4 mb-2">
                            <span className="font-serif text-2xl font-bold text-primary">
                              {step.number}
                            </span>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-sans bg-muted px-2.5 py-1 rounded-md">
                              <Clock className="size-3.5 text-primary" />
                              <span>{step.duration}</span>
                            </div>
                          </div>
                          <CardTitle className="font-serif text-xl md:text-2xl font-normal text-card-foreground">
                            {step.title}
                          </CardTitle>
                          <CardDescription className="font-sans text-sm md:text-base text-muted-foreground pt-2">
                            {step.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-start gap-2.5 mt-2 pt-4 border-t border-border/50 text-xs md:text-sm text-foreground/80 font-sans">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{step.details}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="relative z-10 hidden lg:flex items-center justify-center shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow-md ring-4 ring-background">
                        <Icon className="size-6" />
                      </div>
                    </div>

                    <div className="hidden lg:block lg:w-1/2" />
                  </motion.div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}